import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useQuery } from "@apollo/client";
import { useStoreState } from "ezStore";

import { Card, Table } from "@cofano/cck";
import { Box } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";

import { useFrontendTable } from "components/table";
import DocumentDrawer from "components/documents/DocumentDrawer";

import { DataFormat, hasOptionalFieldPermission, Fields } from "utils";
import { LocationLink } from "utils/LinkUtils";
import { FormattedDate } from "utils/DateUtils";

import { GetReeferChecklistDocument, GetReeferChecklistQuery } from "__generated__/view/voyages/graphql/voyage.queries.generated";
import { VoyageInfoFragment } from "__generated__/view/voyages/graphql/voyage-info.fragments.generated";
import { ChildProps, ChildPropsFromArray } from "types/commons";

type Voyage = ChildProps<GetReeferChecklistQuery, "voyage">;
type Cargo = ChildPropsFromArray<Voyage, "cargos">;
type ReeferChecklist = ChildProps<Cargo, "reeferChecklist">;
type Plugin = ChildProps<ReeferChecklist, "plugin">;
type Plugout = ChildProps<ReeferChecklist, "plugout">;

type Reefer = {
	id: undefined;
	cargo: Cargo
	setPoint?: number | null;
	plugin?: Plugin;
	plugout?: Plugout
}

const VoyageReefersTable = ({ voyage }: { voyage: VoyageInfoFragment }) => {
	const session = useStoreState(store => store.session);

	const navigate = useNavigate();

	const [ reeferExportCargoId, setReeferExportCargoId ] = useState<number | null>(null);

	const { data, loading } = useQuery(GetReeferChecklistDocument, {
		variables: {
			voyageId: voyage.id,
		},
		skip: voyage?.id == null,
	});

	const reefers = (data?.voyage ? data?.voyage.cargos?.map((item => ({
		id: undefined,
		cargo: item,
		setPoint: item.reeferChecklist?.setPoint,
		plugin: item.reeferChecklist?.plugin,
		plugout: item.reeferChecklist?.plugout,
	}))) : []) as Reefer[];

	const { tableProps } = useFrontendTable({
		id: "voyageReefers",
		content: reefers,
		saveForOrganizationEnabled: false,
	});

	return (
		<Card disablePadding>
			<Table
				{...tableProps}
				onRowDoubleClick={(row, event) => {
					const url = `/view/reefers/${(row as Reefer).cargo?.id}/edit`;
					if (event.ctrlKey) {
						window.open(url);
					} else {
						navigate(url);
					}
				}}
				contentLoading={loading}
				columns={[ {
					id: "cargo.containerNumber",
					title: intl.formatMessage({ id: "CONTAINER_NUMBER" }),
					disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "containerNumber"),
					sortable: true,
				}, {
					id: "cargo.containerType",
					title: intl.formatMessage({ id: "CARGO_CONTAINER_TYPE" }),
					render: (row: Reefer) => DataFormat.containerType(row.cargo?.containerType),
					disabled: !hasOptionalFieldPermission(session, Fields.ORDER, "containerType"),
					sortable: true,
					sortKey: "cargo.containerType.isoTypeCode",
					width: 150,
				}, {
					id: "cargo.loadingReference",
					title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
					sortable: true,
				}, {
					id: "cargo.reeferCheckType",
					title: intl.formatMessage({ id: "REEFER_CHECK_TYPE" }),
					render: (row: Reefer) =>  intl.formatMessage({ id: `REEFER_CHECK_TYPE_${row.cargo?.reeferCheckType}` }),
					sortable: true,
				}, {
					id: "cargo.firstLoadingAction.start",
					title: intl.formatMessage({ id: "ORDER_LOADING_DATE" }),
					render: (row: Reefer) => <FormattedDate date={row.cargo?.firstLoadingAction?.start} format="ll" />,
					sortable: true,
				}, {
					id: "cargo.firstLoadingAction.location.description",
					title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
					render: (row: Reefer) => <LocationLink location={row.cargo?.firstLoadingAction?.location} />,
					sortable: true,
				}, {
					id: "cargo.lastDischargingAction.location.description",
					title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
					render: (row: Reefer) => <LocationLink location={row.cargo?.lastDischargingAction?.location} />,
					sortable: true,
				}, {
					id: "cargo.desiredTemperature",
					title: intl.formatMessage({ id: "REEFER_OVERVIEW_TEMPERATURE" }),
					render: (row: Reefer) => DataFormat.temperature(row.cargo?.desiredTemperature),
					sortable: true,
				}, {
					id: "setPoint",
					title: intl.formatMessage({ id: "REEFER_OVERVIEW_SETPOINT" }),
					render: (row: Reefer) => DataFormat.temperature(row.setPoint),
					sortable: true,
				}, {
					id: "plugin",
					title: intl.formatMessage({ id: "PLUGIN" }),
					width: 150,
					render: (row: Reefer) => {

						const inValid = ((row.cargo?.desiredTemperature && row.setPoint) && (row.cargo?.desiredTemperature !== row.setPoint)) || (row.cargo?.loaded && !row.setPoint);

						return (
							<Box
								sx={{
									color: (theme) => inValid ? theme.palette.error.main : undefined,
									fontWeight: inValid ? 600 : undefined,
								}}>
								<strong>
									{DataFormat.temperature(row.plugin?.temperature)}
								</strong>

								<small style={{ marginLeft: 8 }}>
									<FormattedDate date={row.plugin?.timestamp} format="ll HH:mm" />
								</small>
							</Box>
						);
					},
				}, {
					id: "plugout",
					title: intl.formatMessage({ id: "PLUGOUT" }),
					width: 150,
					render: (row: Reefer) => (
						<React.Fragment>
							{row.plugout?.temperature !== 0 ? <strong>
								{DataFormat.temperature(row.plugout?.temperature)}
							</strong> : "" }

							<small style={{ marginLeft: 8 }}>
								<FormattedDate date={row.plugout?.timestamp} format="ll HH:mm" />
							</small>
						</React.Fragment>
					),
				} ]}
				rowActions={[ {
					icon: <PrintIcon />,
					onClick: (row: Reefer) => setReeferExportCargoId(row.cargo?.id ?? null),
				} ]} />

			<DocumentDrawer
				open={reeferExportCargoId !== null}
				url={`/cargos/${reeferExportCargoId}/reefer/export`}
				onClose={() => setReeferExportCargoId(null)}
				downloadedFileName="reefer-protocol-export.pdf"
				multilingual
				blob />
		</Card>
	);
};

export default VoyageReefersTable;
